import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import FontAwesomeIcon from "../../components/FontAwesomeIcon";
import BookingModal from '../../components/BookingModal';

const CraterLakesDrive = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1498931299472-f7a63a5a1cfa?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Crater Lakes Scenic Drive
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Explore volcanic lakes with panoramic Rwenzori views near Queen Elizabeth National Park
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(93 reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-water" className="w-4 h-4 mr-2" />
                10+ Crater Lakes
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-mountain" className="w-4 h-4 mr-2" />
                Rwenzori Panoramas
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Scenic Drive
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Uganda's Most Scenic Drive
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The crater lakes region north of Queen Elizabeth National Park is one of East Africa's most visually stunning landscapes—a rolling plateau dotted with dozens of explosion craters now filled with jewel-colored lakes, each reflecting different mineral compositions and algae populations in shades of emerald, turquoise, deep blue, and even pink.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  These craters formed 8,000-10,000 years ago during violent volcanic eruptions that peppered the Western Rift Valley. Today they host tranquil lakes surrounded by lush vegetation, with the snow-capped Rwenzori Mountains providing a spectacular backdrop when clear skies allow. The scenic drive takes you along winding roads that crest hills offering panoramic crater views, descend into valleys, and pass through traditional villages.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Lake Katwe, the largest and most historically significant crater lake, has been mined for salt since at least the 14th century. Stopping here offers fascinating insights into traditional salt extraction methods still practiced today. You witness workers wading in shallow brine pools, using wooden tools to scrape salt crystals—a practice unchanged for centuries.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Drive Highlights
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-water', title: '10+ Explosion Craters', description: 'See lakes of emerald, turquoise, blue, and pink' },
                  { icon: 'fa-mountain', title: 'Rwenzori Views', description: 'Snow-capped peaks backdrop on clear mornings' },
                  { icon: 'fa-camera', title: 'Photography Paradise', description: 'Panoramic viewpoints and dramatic landscape compositions' },
                  { icon: 'fa-users', title: 'Lake Katwe Salt Mining', description: 'Witness ancient salt extraction methods still in use' },
                  { icon: 'fa-leaf', title: 'Nature Walk Options', description: 'Optional crater rim trails with forest and primates' },
                  { icon: 'fa-book', title: 'Geological Education', description: 'Learn about volcanic formation and crater types' },
                  { icon: 'fa-home', title: 'Traditional Villages', description: 'Pass through communities living alongside craters' },
                  { icon: 'fa-sun', title: 'Relaxed Pacing', description: 'Gentle half-day tour perfect for all ages' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Half-Day Tour Timeline
              </h2>
              <div className="space-y-6">
                {[
                  { time: '08:00 AM', title: 'Departure', desc: 'Leave accommodation, drive toward crater field with Rwenzori views.' },
                  { time: '08:45 AM', title: 'First Panorama', desc: 'Elevated viewpoint overlooking 5-7 craters simultaneously.' },
                  { time: '09:30 AM', title: 'Lake Katwe Visit', desc: 'Traditional salt mining observation and cultural insights.' },
                  { time: '10:15 AM', title: 'Multiple Lake Stops', desc: 'Visit various colored lakes: emerald, turquoise, pink.' },
                  { time: '11:00 AM', title: 'Lake Nkuruba Walk', desc: 'Optional 30-minute crater rim trail through forest.' },
                  { time: '11:45 AM', title: 'Final Viewpoint', desc: 'Best Rwenzori panorama (weather permitting), return journey.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time.split(' ')[0]}<br />{step.time.split(' ')[1]}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Common Questions
              </h2>
              <div className="space-y-6">
                {[
                  { q: 'Best time for photography?', a: 'Early morning (07:00-11:00 AM) offers best Rwenzori visibility before afternoon clouds build. Light is soft and golden.' },
                  { q: 'Can we swim in crater lakes?', a: 'Lake Nkuruba is bilharzia-free and safe for swimming. Most other lakes are not recommended due to health concerns or cultural sensitivities.' },
                  { q: 'How many craters will we see?', a: 'Half-day tour visits 5-8 main craters. Full-day tours cover 10-12 craters. Over 40 explosion craters exist in the region.' },
                  { q: 'Is this suitable for children?', a: 'Yes, excellent for all ages. Most viewing is from the vehicle or short walks to viewpoints. Gentle pacing suits everyone.' }
                ].map((faq, index) => (
                  <div key={index} className="border-l-4 border-safari-gold pl-6 py-4">
                    <h3 className="text-xl font-bold text-safari-green mb-2">{faq.q}</h3>
                    <p className="text-gray-700">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Queen Elizabeth Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/queen-elizabeth-game-drives" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800"
                      alt="Game Drive"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Game Drives</h3>
                    <p className="text-gray-700 mb-4">Spot lions, elephants, and leopards on the savanna.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/kazinga-boat-safari" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1576485290814-1c72aa4bbb8e?w=800"
                      alt="Kazinga Channel"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Kazinga Boat Safari</h3>
                    <p className="text-gray-700 mb-4">See hippos, elephants, and crocs from the water.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 3-4 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Morning (7-11am)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Price:</strong> $60 per person</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Easy</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Private vehicle</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> All ages</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Scenic Drive
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Private safari vehicle",
                    "Experienced driver/guide",
                    "Lake Katwe salt tour",
                    "Bottled water",
                    "Hotel pickup/drop-off"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Tips for guide",
                    "Meals (unless full day)",
                    "Personal insurance"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> Easy. Mostly driving with short walks.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Health:</strong> No specific requirements.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Crater Lakes Scenic Drive"
      />
    </div>
  );
};

export default CraterLakesDrive;
